import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import Client from "data/_client"
import { diff_obj } from 'data/lib/diff'
import notificationService from "data/notification"
import { local } from 'data/lib/local-json'

import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "data/validate"

// private module logic //
export type UserGroup = {
  code: string
  usergroupid: number
}

interface UserGroups {
  error?: string;
  groups: UserGroup[]
}

const profile = local("auth.profile")
const orgId   = _.get(profile, "organizationId");
let userGroupMap = {}

const initialState: UserGroups = { groups: [] }
const userGroupStream = new BehaviorSubject<UserGroups>(initialState)
const userGroupEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  userGroupStream.next({ groups: [] })
  userGroupMap = {}
}

function get(usergroupid: number): UserGroup {
  return userGroupMap[usergroupid]
}

const xfrmUnauth = (message) => message == 'Unauthorized' ? 'Administrator application role required to modify user groups' : message

function create(): Promise<UserGroup | { message: string }> {
  const currentValue = userGroupStream.getValue().groups
  userGroupStream.next({ groups: currentValue })

  const profile = local("auth.profile")
  const orgId = profile.organizationId;
  const payload = {
    organizationid: orgId
  }

  return Client.post(`usergroup`, payload).then((userGroup: UserGroup) => {
    push_undo({
      undofunc: () => deleteOne(userGroup.usergroupid),
      redofunc: () => create()
    })
    userGroupMap[userGroup.usergroupid] = userGroup
    // merge results unique by usergroupid
    const currentValue = userGroupStream.getValue().groups
    const [system, nulls, rest] = sortFn(currentValue, 'code', 'asc', true)
    userGroupStream.next({
      groups: [userGroup, ...system, ...nulls, ...rest]
    })
    userGroupEventStream.next({ type: 'USERGROUP_CREATED', payload })
    return userGroup
  })
  .catch((err: { message: string }) => {
    // on error overwrite optimistic update
    userGroupStream.next({error: err.message, groups: currentValue})
    notificationService.notify({ type: 'danger', message: xfrmUnauth(err.message), dismiss: 5000 })
    fetchUserGroups()
    return err
  })
}

async function deleteOne(
  usergroupid: number,
  force=false
): Promise<boolean | { message: string }> {
  const currentValue = userGroupStream.getValue().groups

  const deleteFn = () => deleteOne(usergroupid, true)
  const refreshFn = () => fetchUserGroups()
  if (!force && !(await handleReferences('usergroup', usergroupid, {deleteFn, refreshFn, canForce: true}))) {
    return false
  }

  // filter out the deleted usergroupid
  const value = _(currentValue)
    .filter((p) => p.usergroupid !== usergroupid)
    .value()
  value.forEach((p) => (userGroupMap[p.usergroupid] = p)) // ? the delete is below, so what does this do?

  userGroupStream.next({ groups: sortFn(value) })

  // update the db
  return _patches_.all(usergroupid).then(() =>
  Client.del(`usergroup/${usergroupid}`)
    .then((userGroup: UserGroup) => {
      delete userGroupMap[userGroup.usergroupid]
      push_undo({
        undofunc: () => {
          Client.post('userGroup', userGroup)
          userGroupMap[userGroup.usergroupid] = userGroup
          userGroupStream.next({ groups: currentValue })
        },
        redofunc: () => deleteOne(usergroupid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      userGroupStream.next({ error: err.message, groups: currentValue })
      notificationService.notify({
        type: 'danger',
        message: xfrmUnauth(err.message),
        dismiss: 5000
      })
      fetchUserGroups()
      return err
    })
    .finally(() => {
      validationService.fetchAll('usergroup')
    })
  )
}

function fetchUserGroups(): Promise<UserGroup[] | { message: string }> {
  if (_fetches_.has(orgId)) return _fetches_.first(orgId)

  clear()
  userGroupStream.next({ groups: sortFn(userGroupStream.getValue().groups) })

  return _fetches_.watch(orgId, Client.get('usergroup')
  .then((records: UserGroup[]) => {
    _.forEach(records, (userGroup) => (userGroupMap[userGroup.usergroupid] = userGroup))
    userGroupStream.next({ groups: sortFn(records) })
    return records
  })
  .catch((error: { message: string }) => {
    notificationService.notify({ type: 'danger', message: error.message, dismiss: 5000 })
    userGroupStream.next({ groups: userGroupStream.getValue().groups, error: error.message })
    return error
  }))
}

function patch(usergroup: UserGroup): Promise<UserGroup | { message: string }> {
  const currentValue = userGroupStream.getValue().groups
  const usergroupid = usergroup.usergroupid
  const currentUserGroup = _.find(currentValue, { usergroupid })
  const changeset = diff_obj(usergroup, currentUserGroup)

  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentUserGroup),
    redofunc: () => patch(usergroup)
  })

  // optimistic update
  let nextGroups = _.cloneDeep(currentValue)
  nextGroups.splice(_.findIndex(nextGroups, userGroup => userGroup.usergroupid === usergroupid), 1, usergroup)
  userGroupStream.next({
    groups: nextGroups
  })

  return _patches_.watch(usergroupid, Client.patch(`usergroup/${usergroupid}`, changeset)
    .then((userGroup: UserGroup) => {
      userGroupMap[userGroup.usergroupid] = userGroup
      return userGroup
    })
    .catch((error: { message: string }) => {
      // on error overwrite optimistic update
      userGroupStream.next({ error: error.message, groups: currentValue })
      notificationService.notify({ type: 'danger', message: xfrmUnauth(error.message), dismiss: 5000 })
      return error
    })
    .finally(() => {
      validationService.fetchAll('usergroup')
    })
  )
}

const sortFn = (data, field='code', order='asc', partitioned=false) => {
  let [system, rest] = _.partition(data, ({code}) => _.startsWith(code, '*'))
  let [nulls, nonnulls] = _.partition(rest, ({code}) => _.isNil(code))
  system = _.orderBy(system, 'code', 'asc')
  nonnulls = _.orderBy(nonnulls, (x) => x[field] || '', order)
  return partitioned ? [system, nulls, nonnulls] : [...system, ...nulls,  ...nonnulls]
}

// public module interface //

const userGroupService = {
  stream: () => userGroupStream.asObservable(),
  value: () => userGroupStream.getValue().groups,
  eventStream: () => userGroupEventStream.asObservable(),
  clear,
  create,
  deleteOne,
  fetchUserGroups,
  get,
  patch,
  sortFn
}

export default userGroupService
